import { Decimal } from '@starsoft/common/services/Decimal';
import { BaseEntity } from '../base-entity';
import { Coin } from '../coin';

/**
 * Entity representing a snapshot of multiple currency conversion rates for a cryptocurrency.
 *
 * This entity stores point-in-time conversion rates between a cryptocurrency and various fiat currencies.
 * It's useful for historical rate tracking, financial reporting, and cross-currency calculations.
 *
 * @property {Coin} coin - The cryptocurrency this snapshot relates to
 * @property {bigint} coinId - Foreign key to the Coin entity
 * @property {Decimal} originalAmount - The base amount of the cryptocurrency
 * @property {Record<string, Decimal>} convertedAmounts - Mapping of currency codes to converted values
 */
export class MultiExchangeRateSnapshot extends BaseEntity {
  /**
   * Many-to-One relationship with the Coin entity.
   * Indexed for optimized query performance.
   * This indicates which cryptocurrency this exchange rate snapshot belongs to.
   *
   * The relationship is essential for tracking which digital asset's rates are being recorded
   * and allows for efficient querying of rate history by coin.
   */
  coin: Coin;

  /**
   * Column to store the coin ID.
   * Uses bigint type with a custom transformer for handling large numbers.
   * This is the foreign key linking to the Coin entity.
   *
   * The BigintTransformer ensures proper conversion between JavaScript bigint and
   * PostgreSQL bigint types, maintaining numeric precision for large IDs.
   */
  coinId: bigint;

  /**
   * Column to store the original amount of the cryptocurrency.
   * Uses a custom Decimal type to ensure precision in financial calculations.
   *
   * This represents the base amount that is converted to other currencies.
   * For example, this might store 1.0 to represent conversion rates for one unit
   * of the cryptocurrency, or a specific amount for a particular transaction.
   *
   * The DecimalColumn decorator ensures that decimal values are stored with full precision,
   * which is critical for financial applications.
   */
  originalAmount: Decimal;

  /**
   * Column to store the converted amounts in different fiat currencies.
   * Uses JSONB type to store a mapping of currency codes to Decimal values.
   *
   * For example: { "USD": Decimal(1234.56), "EUR": Decimal(1045.67), "JPY": Decimal(135000.00) }
   *
   * This structure allows for efficient storage of multiple currency conversions in a single record,
   * enabling applications to display values in the user's preferred currency without additional
   * conversion calculations. The JSONB format provides both storage efficiency and query flexibility.
   */
  convertedAmounts: Record<string, Decimal>;
}
